import { useTranslation } from 'react-i18next';
import { useBasePath } from '~/common/hooks/useBasePath';
import { useForm } from '@conform-to/react';
import { parseWithZod } from '@conform-to/zod/v4';
import { createTemplateSchema } from '~/features/templates/schemas/createTemplateSchema';
import { useCallback, useState } from 'react';
import { Button } from '~/common/components/ui/button';
import { FORM_FIELDS } from '~/common/constants/formfields';
import { DialogClose, DialogFooter } from '~/common/components/ui/dialog';
import ProgressButton from '~/common/components/ProgressButton';
import { cn } from '~/common/utils/styleUtil';
import { toast } from 'sonner';
import { LimitedInput } from '~/common/components/LimitedInput';
import { clientFetch } from '~/common/utils/clientFetch.client';
import { useSendAddToListToGoogleAnalytics } from '~/tracking/googleAnalytics';
import type { AddArticleToTemplateInput } from '~/features/templates/routes/resources/AddArticleToTemplateRoute';

export function TemplateCreate({
  onClose,
  articles,
  isAgh
}: Readonly<{
  onClose: () => void;
  articles: AddArticleToTemplateInput[];
  isAgh: boolean;
}>) {
  const { t } = useTranslation();
  const { path } = useBasePath();
  const [inProgress, setInProgress] = useState<boolean>(false);
  const [responseData, setResponseData] = useState<any>(null);
  const sendAddToListToGoogleAnalytics = useSendAddToListToGoogleAnalytics();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [form, fields] = useForm({
    lastResult: responseData,
    shouldValidate: 'onBlur',
    shouldRevalidate: 'onInput',
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: createTemplateSchema });
    }
  });

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();

      try {
        setInProgress(true);
        const formData = new FormData(event.target);
        formData.append(FORM_FIELDS.Intent, FORM_FIELDS.Create);

        const response = await clientFetch(`${path}/resources/templates/addarticletotemplate`, {
          method: 'POST',
          body: formData
        });

        const data = (await response.json()) as { ok: boolean; message: string };
        setResponseData(data);
        if (data.message) {
          if (data.ok) {
            sendAddToListToGoogleAnalytics(articles);
            toast.success(data.message);
            onClose();
          } else {
            toast.error(data.message);
          }
        }
      } catch {
        toast.error(t('Ein technischer Fehler ist aufgetreten. Bitte laden Sie den Webshop neu!'));
      } finally {
        setInProgress(false);
      }
    },
    [articles, onClose, path, sendAddToListToGoogleAnalytics, t]
  );

  return (
    <form
      method="post"
      className={cn('flex flex-col h-full desktop:justify-between desktop:h-full')}
      onSubmit={handleSubmit}
    >
      <input name="articles" type="hidden" defaultValue={JSON.stringify(articles)} />

      <div className="flex-1">
        <p className="my-2">{t(isAgh ? 'Bezeichnung der Einkaufsliste' : 'Bezeichnung der Bestellvorlage')}</p>
        <LimitedInput
          name={fields.name.name}
          defaultValue={fields.name.initialValue ?? ''}
          testId="template-create-input"
          limit={40}
          placeholder={t(isAgh ? 'Einkaufslistenname eingeben' : 'Vorlagenname eingeben')}
        />
        <div className="mt-1 min-h-6 text-sm text-primary">{fields.name.errors?.map(e => t(e as any))}</div>
      </div>

      <DialogFooter className={'flex-col desktop:flex-row gap-2 desktop:gap-0'}>
        <>
          <DialogClose asChild>
            <Button type="button" variant="primaryOutline" onClick={onClose} data-testid="template-create-cancel">
              {t('Abbrechen')}
            </Button>
          </DialogClose>
          <ProgressButton
            name={FORM_FIELDS.Intent}
            value={FORM_FIELDS.Create}
            type="submit"
            data-testid="template-create-submit"
            inProgress={inProgress}
          >
            {t('Erstellen & hinzufügen')}
          </ProgressButton>
        </>
      </DialogFooter>
    </form>
  );
}
